import { Button, Chip, CircularProgress, InputLabel, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import DeleteConfirmationModalContainer from "./DeleteConfirmationModalContainer";
import { chipColors } from "./constants";

const StyledDeleteButton = styled(Button)({
    color: "#fff",
    backgroundColor: "#d32f2f",
    "&:hover": {
        backgroundColor: "#c62828",
    },
});
const StyledFormLabel = styled(InputLabel)({
    fontSize: "13px",
    color: "#9E9E9E",
    textTransform: "uppercase",
});

const StyledAddConditionButton = styled(Button)({
    margin: "2.5vh 0",
});

const StyledPaper = styled("div")({
    display: "flex",
    flexDirection: "column",
    background: "white",
    top: "50%",
    left: "50%",
    width: "1300px",
    height: "700px",
    transform: "translate(-50%, -50%)",
    position: "absolute",
    borderRadius: "3px",
    boxShadow: " 0 3px 7px rgba(0, 0, 0, 0.3)",
    padding: "20px",
});

const StyledModalHeader = styled(Typography)({
    fontWeight: "600",
    marginBottom: "20px",
});

const StyledButtonContainer = styled(Button)({
    padding: "7px 16px",
    marginTop: "22px",
    marginLeft: "0px",
    width: "auto",
});

const StyledChipContainer = styled("div")({
    border: "1px solid #e7e7e7",
    borderRadius: "5px",
    height: "300px",
    marginTop: "10px",
    overflow: "auto",
});

const StyledChip = styled(Chip)({
    margin: "5px",
});

const ComplianceRuleModal = (props) => {
    const {
        modalHeader,
        isLoading,
        conditions,
        conditionValues,
        actionValues,
        selectConditions,
        selectConditionType,
        handleActionsSelect,
        addCondition,
        removeCondition,
        addAction,
        removeAction,
        handleRuleInputChange,
        handleSaveRule,
        modal,
        handleClose,
    } = props;

    const [openDeleteModal, setDeleteModal] = useState(false);

    const filterOptionsByDisplay = (options) => {
        const display = options.filter((option) => {
            return option.display;
        });
        return display;
    };

    const operatorOptions = (conditionTypeKey) => {
        if (conditionTypes.length < 1 || conditionTypeKey === 0) {
            return [];
        }
        let result = conditionTypes.find((conditionType) => {
            return conditionType.value === conditionTypeKey;
        });
        return result.conditionOperators.length > 0 ? result.conditionOperators : [];
    };

    const statesOptions = () => {
        return isDisabled(conditionTypeKey) ? [] : states;
    };

    const isDisabled = (conditionTypeKey) => {
        if (conditionTypes.length < 1 || conditionTypeKey === 0) {
            return true;
        }
        let result = conditionTypes.find((conditionType) => {
            return conditionType.value === conditionTypeKey;
        });
        return result.conditionOperators.length < 1;
    };

    const { conditionTypes, states, actionTypes, documentTypes } = conditions;
    const { conditionTypeKey, conditionOperatorKey, conditionValue } = conditionValues;
    const { documentType, actionType } = actionValues;
    const { ruleKey, ruleName, ruleDescription } = modal.data;

    return !isLoading ? (
        <React.Fragment>
            <StyledPaper>
                <StyledModalHeader variant="h6">{modalHeader}</StyledModalHeader>
                <div style={{ display: "flex", gap: "1vw" }}>
                    <div style={{ flex: "1 1 auto" }}>
                        <BSInput
                            noHelperText
                            label="Rule Name"
                            onChange={(e) => handleRuleInputChange("ruleName", e.target.value)}
                            value={ruleName}
                        />
                    </div>
                    <div style={{ flex: "1 1 auto" }}>
                        <BSInput
                            noHelperText
                            label="Rule Description"
                            onChange={(e) => handleRuleInputChange("ruleDescription", e.target.value)}
                            value={ruleDescription}
                        />
                    </div>
                </div>
                <div>
                    <StyledModalHeader variant="h6">Add Conditions and Actions</StyledModalHeader>
                    <div style={{ display: "flex", gap: "0.5vw" }}>
                        <div style={{ display: "flex", gap: "1vw", width: "50%" }}>
                            <div data-testid="conditionTypeDropdown" style={{ flex: "2 2 0" }}>
                                <BSSelect
                                    label="Condition Type"
                                    value={conditionTypeKey}
                                    options={filterOptionsByDisplay(conditionTypes)}
                                    onChange={(e) => {
                                        selectConditionType(e.target.value);
                                    }}
                                />
                            </div>
                            <BSSelect
                                label="Operator"
                                value={conditionOperatorKey}
                                disabled={isDisabled(conditionTypeKey)}
                                options={operatorOptions(conditionTypeKey)}
                                onChange={(e) => {
                                    selectConditions("conditionOperatorKey", Number(e.target.value));
                                }}
                                extraFormControlStyles={{ flex: "1 1 0" }}
                            />

                            <BSSelect
                                label="Value"
                                value={conditionValue}
                                disabled={isDisabled(conditionTypeKey)}
                                options={statesOptions()}
                                onChange={(e) => selectConditions("conditionValue", e.target.value)}
                                extraFormControlStyles={{ flex: "1 1 0" }}
                            />
                            <StyledAddConditionButton
                                disabled={conditionTypeKey === 0}
                                variant="contained"
                                color="primary"
                                onClick={addCondition}
                            >
                                Add Condition
                            </StyledAddConditionButton>
                        </div>
                        <div style={{ display: "flex", gap: "1vw", width: "50%", paddingLeft: "0.5vw" }}>
                            <div data-testid="documentTypeDropdown" style={{ flex: "3 3 0" }}>
                                <BSSelect
                                    label="Document Type"
                                    value={documentType}
                                    options={filterOptionsByDisplay(documentTypes)}
                                    onChange={(e) => {
                                        handleActionsSelect("documentType", e.target.value);
                                    }}
                                />
                            </div>
                            <BSSelect
                                label="Action Type"
                                value={actionType}
                                options={actionTypes}
                                onChange={(e) => {
                                    handleActionsSelect("actionType", e.target.value);
                                }}
                                extraFormControlStyles={{ flex: "2 2 0" }}
                            />
                            <div>
                                <StyledButtonContainer
                                    disabled={documentType === 0}
                                    variant="contained"
                                    color="primary"
                                    onClick={addAction}
                                >
                                    Add Action
                                </StyledButtonContainer>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "1vw" }}>
                        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                            <StyledFormLabel shrink>Conditions</StyledFormLabel>
                            <StyledChipContainer data-testid="conditionTypes">
                                {modal.data.conditions.map((condition, i) => {
                                    const { conditionType, conditionOperator, conditionValue } = condition;
                                    const label =
                                        conditionValue !== null && conditionOperator !== null
                                            ? `${conditionType.displayName} ${conditionOperator.displayName} ${conditionValue}`
                                            : conditionType.displayName;
                                    return (
                                        <StyledChip
                                            variant="outlined"
                                            onDelete={() => {
                                                removeCondition(conditionType.conditionTypeKey);
                                            }}
                                            style={{ border: `0.2em solid ${chipColors.hidden}` }}
                                            key={i}
                                            label={label}
                                            data-testid="removeConditionType"
                                        />
                                    );
                                })}
                            </StyledChipContainer>
                            <div style={{ display: "flex", gap: "1vw", paddingTop: "1vh" }}>
                                <Chip
                                    variant="outlined"
                                    style={{
                                        border: `0.2em solid ${chipColors.required}`,
                                        color: chipColors.required,
                                    }}
                                    label={"Document is Required"}
                                />

                                <Chip
                                    variant="outlined"
                                    style={{
                                        border: `0.2em solid ${chipColors.optional}`,
                                        color: chipColors.optional,
                                    }}
                                    label={"Document is Optional"}
                                />

                                <Chip
                                    variant="outlined"
                                    style={{
                                        border: `0.2em solid ${chipColors.hidden}`,
                                        color: chipColors.hidden,
                                    }}
                                    label={"Don't Show Document as an Option"}
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                            <StyledFormLabel shrink>Actions</StyledFormLabel>
                            <StyledChipContainer data-testid="actionTypes">
                                {modal.data.actions
                                    .sort((a, b) => a.actionType.actionTypeKey - b.actionType.actionTypeKey)
                                    .map((action, i) => {
                                        const { actionType, documentTypeKey, documentName } = action;
                                        const chipColor =
                                            actionType.actionTypeKey === 1
                                                ? chipColors.required
                                                : actionType.actionTypeKey === 2
                                                  ? chipColors.optional
                                                  : chipColors.hidden;
                                        return (
                                            <StyledChip
                                                variant="outlined"
                                                onDelete={() => {
                                                    removeAction(documentTypeKey);
                                                }}
                                                style={{
                                                    border: `0.2em solid ${chipColor}`,
                                                    color: chipColor,
                                                }}
                                                key={i}
                                                label={documentName}
                                                data-testid="removeActionType"
                                            />
                                        );
                                    })}
                            </StyledChipContainer>
                            <div style={{ alignSelf: "end", paddingTop: "1vh" }}>
                                <Button variant="outlined" onClick={handleClose} color="primary">
                                    Cancel
                                </Button>
                                {ruleKey ? (
                                    <StyledDeleteButton variant="contained" onClick={() => setDeleteModal(true)}>
                                        Delete
                                    </StyledDeleteButton>
                                ) : null}
                                <Button variant="contained" onClick={() => handleSaveRule(ruleKey)} color="primary">
                                    Save Rule
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </StyledPaper>
            <DeleteConfirmationModalContainer open={openDeleteModal} toggleModal={setDeleteModal} />
        </React.Fragment>
    ) : (
        <CircularProgress style={{ position: "fixed", top: "50%", left: "50%" }} />
    );
};

export default ComplianceRuleModal;
